import React, { useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";
import { AlertContext } from "../components/alert/AlertProvider";
import { useDispatch, useSelector } from "react-redux";
import { makeStyles } from "@material-ui/styles";
import { HIDE_VIEWER } from "media-viewer/mediaViewerRedux";
import { useHeadRequest } from "../common/fetchServices";
import { getIiifUrl } from "../creator-application/content/contentServices";

import AppBar from "@material-ui/core/AppBar";
import Close from "@material-ui/icons/Close";
import Dialog from "@material-ui/core/Dialog";
import IconButton from "@material-ui/core/IconButton";
import Button from "@material-ui/core/Button";
import Toolbar from "@material-ui/core/Toolbar";
import Typography from "@material-ui/core/Typography";
import ZoomInIcon from "@material-ui/icons/ZoomIn";
import ZoomOutIcon from "@material-ui/icons/ZoomOut";
import ZoomOutMap from "@material-ui/icons/ZoomOutMap";

import OpenSeadragon from "openseadragon";
import LoadingIndicator from "../components/LoadingIndicator";

const useStyles = makeStyles(theme => ({
  appBar: {
    backgroundColor: "white",
    boxShadow: "none",
  },

  appBarBottom: {
    top: "auto",
    bottom: 0,
    backgroundColor: "white",
    boxShadow: "none",
  },

  originalSizeButton: {
    fontSize: "1.6rem",
    minWidth: 42,
    color: "rgba(0, 0, 0, .54)",
    borderRadius: "50%",
    padding: "8px 12px",
  },

  title: {
    textAlign: "center",
    flex: 1,
    color: theme.palette.text.secondary,
  },

  viewer: {
    height: "100%",
    width: "100%",
  },

  viewerContainer: {
    borderTop: `1px solid ${theme.palette.grey[300]}`,
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
    backgroundColor: theme.palette.grey[100],
    flex: 1,
    position: "relative",
    overflow: "hidden",
  },

  viewport: {
    position: "absolute",
    top: 10,
    right: 10,
    bottom: 10,
    left: 10,
  },

  zoomLabel: {
    marginLeft: 12,
    color: theme.palette.grey[900],
    borderRadius: 4,
    padding: "0 6px",
    backgroundColor: theme.palette.grey[300],
    textAlign: "center",
  },
}));

const config = {
  id: "viewer",
  /**
   * Gesture Settings
   * set clickToZoom: false to disable click zoom behavior
   */
  // gestureSettingsMouse: {
  //   clickToZoom: false,
  // },
  // gestureSettingsPen: {
  //   clickToZoom: false,
  // },

  /**
   * OpenSeadragon 5.0.0 added WebGL support by default, but this resulted in terrible performance particularly on mobile devices.
   * "canvas" provides much better performance, at least for non-tiled images. Toggle this
   */
  drawer: "canvas",

  // Map custom buttons to OpenSeadragon buttons
  zoomInButton: "image-viewer-zoom-in",
  zoomOutButton: "image-viewer-zoom-out",
  homeButton: "image-viewer-zoom-to-fit",

  // Zoom Settings
  maxZoomPixelRatio: 10,
  minZoomImageRatio: 0.5,
};

const ImageViewerDialog = () => {
  const classes = useStyles();

  const { alertError } = useContext(AlertContext);
  const { media } = useSelector(state => state.mediaViewer);
  const auth = useSelector(state => state.auth);
  const dispatch = useDispatch();
  const onClose = () => dispatch({ type: HIDE_VIEWER });

  // OpenSeadragon doesn't provide request/response info on errors,
  // so manually check for expired token
  useHeadRequest(media.imageUrl);

  const [loading, setLoading] = useState(true);
  const [viewerEl, setViewerEl] = useState(null);
  const [viewer, setViewer] = useState(null);
  const [zoom, setZoom] = useState(0);

  /**
   * returns maximum zoom level for image based on image size with respect to image viewport container
   * @param {OpenSeadragon.TileSource}
   * @return {Number}
   */
  const calculateFullSizeZoom = useCallback(
    tileSource => {
      if (viewer) {
        return tileSource.dimensions.x / viewer.viewport.getContainerSize().x;
      }
    },
    [viewer],
  );

  /**
   * zoom image so that pixels in image and pixels on screen are 1:1
   */
  const zoomToFullSize = () => {
    const tiledImage = viewer.world.getItemAt(0);
    const maxZoom = calculateFullSizeZoom(tiledImage.source);
    viewer.viewport.zoomTo(maxZoom);
  };

  useEffect(() => {
    if (viewerEl) {
      if (!viewer) {
        setViewer(OpenSeadragon(config));
      }
    }

    return () => viewer?.close();
  }, [viewer, viewerEl]);

  useEffect(() => {
    if (viewer && media) {
      /**
       * For IIIF urls, just pass directly to tileSource -> tileSource: https://iiif-test.biblioboard.com/iiif/3/SID-0000000044239/SID-0000000044239/info.json
       * @param {String|Object} tileSource
       * @returns {Promise}
       */
      const loadImage = tileSource => {
        return new Promise((resolve, reject) => {
          viewer.addTiledImage({
            tileSource: tileSource,
            success: event => {
              // "string" means info.json url
              if (typeof tileSource === "string") {
                const url = new URL(tileSource);
                // get signed url parameters (Policy, Signature, Key-Pair-Id) from info.json to pass to each tile request
                const params = new URLSearchParams(url.search);
                const source = event.item.source;
                const originalGetTileUrl = source.getTileUrl.bind(source);
                source.getTileUrl = function (level, x, y) {
                  const url = originalGetTileUrl(level, x, y);
                  return `${url}?${params.toString()}`;
                };
              }

              resolve();
            },
            error: reject,
          });
        });
      };

      (async () => {
        try {
          const iiifUrl = getIiifUrl("/info.json", {
            media,
            token: auth.user.token,
          });
          const { data } = await axios(iiifUrl);

          try {
            // we don't currently have a way to reliably determine if a IIIF resource exists
            // other than trying to fetch it from CloudFront
            await loadImage(data.iiifUrl);
          } catch (err) {
            // IIIF image server only returns 500 errors, even if 404 is more appropriate
            // we'll fall back to imageUrl if any error occurs
            await loadImage({
              type: "image",
              url: media.imageUrl,
              crossOriginPolicy: "Anonymous",
              ajaxWithCredentials: false,
            });
          }

          // Add adjustments, if any, below once image has loaded
        } catch (error) {
          console.log("An error occurred while loading image: ", error);
          alertError({
            message:
              "An error occurred whille loading the image. Try refreshing your browser. If the problem persists, please email support@biblioboard.com",
          });
        } finally {
          setLoading(false);
        }
      })();
    }
  }, [alertError, auth.user, media, viewer]);

  useEffect(() => {
    if (viewer) {
      viewer.addHandler("zoom", event => {
        setZoom(Math.round(event.zoom * 100));
      });

      return () => {
        viewer.removeAllHandlers("zoom");
      };
    }
  }, [viewer]);

  return (
    <Dialog
      fullScreen={true}
      onClose={onClose}
      aria-label="image viewer"
      open={true}
    >
      {loading && <LoadingIndicator transparent />}
      <AppBar className={classes.appBar} position="relative">
        <Toolbar variant="dense">
          <Typography variant="h5" className={classes.title}>
            {media.title}
          </Typography>
          <IconButton edge="end" onClick={onClose} aria-label="close">
            <Close />
          </IconButton>
        </Toolbar>
      </AppBar>
      <div className={classes.viewerContainer}>
        <div className={classes.viewport}>
          <div id="viewer" ref={setViewerEl} className={classes.viewer}></div>
        </div>
      </div>
      <AppBar position="relative" className={classes.appBarBottom}>
        <Toolbar variant="dense">
          <IconButton id="image-viewer-zoom-in" aria-label="zoom in">
            <ZoomInIcon />
          </IconButton>

          <IconButton id="image-viewer-zoom-out" aria-label="zoom out">
            <ZoomOutIcon />
          </IconButton>

          <IconButton id="image-viewer-zoom-to-fit" aria-label="zoom to fit">
            <ZoomOutMap />
          </IconButton>

          <Button
            id="image-viewer-zoom-one-to-one"
            className={classes.originalSizeButton}
            aria-label="zoom to original size"
            onClick={zoomToFullSize}
          >
            1:1
          </Button>

          {viewer && (
            <Typography variant="subtitle1" className={classes.zoomLabel}>
              {`${zoom}%`}
            </Typography>
          )}
        </Toolbar>
      </AppBar>
    </Dialog>
  );
};

export default ImageViewerDialog;
